import { z } from 'zod';
import type { ActivityLogicPlugin } from '../../core/types/plugin.base.types';
import type { MathEquationPayload, MathEquationRuntime } from '../../core/types/payloads/math_equation.types';
import type { HostActionPayload, StudentInputEnvelope } from '../../core/types/events.types';
import type { RoutingMode } from '../../core/types/room.types';
import { MathEquationPayloadSchema } from '../../core/schemas/examGenerationSchema';

export const EQUATION_BLANK_PATTERN = /\\square|\{\{box\}\}/g;

export function normalizeEquationLatex(raw: string): string {
  return String(raw || '')
    .replace(/\{\{box\}\}/g, '\\square')
    .replace(/\s+/g, ' ')
    .trim();
}

export function splitEquationWithBlanks(equationLatex: string): Array<{ type: 'math' | 'blank'; value: string }> {
  const normalized = normalizeEquationLatex(equationLatex);
  if (!normalized) return [];

  const segments: Array<{ type: 'math' | 'blank'; value: string }> = [];
  const regex = /\\square/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(normalized)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'math', value: normalized.slice(lastIndex, match.index) });
    }
    segments.push({ type: 'blank', value: '' });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < normalized.length) {
    segments.push({ type: 'math', value: normalized.slice(lastIndex) });
  }

  if (!segments.length) {
    segments.push({ type: 'math', value: normalized });
  }

  return segments;
}

export function validateMathEquationPayload(raw: unknown): MathEquationPayload {
  const parsed = MathEquationPayloadSchema.safeParse(raw);
  if (!parsed.success) {
    const first = parsed.error.issues[0];
    throw new Error(first?.message || 'MathEquation payload invalid');
  }

  const data = parsed.data;
  const resolutionSteps = data.resolutionSteps?.map((step) => step.trim()).filter(Boolean);

  return {
    instruction: data.instruction.trim(),
    equationLatex: normalizeEquationLatex(data.equationLatex),
    ...(resolutionSteps?.length ? { resolutionSteps } : {}),
  };
}

export function createInitialMathEquationRuntime(
  _payload: MathEquationPayload,
  _routing: RoutingMode,
): MathEquationRuntime {
  return { viewed: false };
}

export const mathEquationLogic: ActivityLogicPlugin<'math_equation'> = {
  mode: 'math_equation',
  validatePayload: validateMathEquationPayload,
  createInitialRuntime: createInitialMathEquationRuntime,

  onStudentInput(ctx, input: StudentInputEnvelope) {
    if (input.action === 'EXAM_CONTINUE' || input.action === 'VIEW') {
      return { runtime: { ...ctx.runtime, viewed: true } };
    }
    return { runtime: ctx.runtime, rejected: true, rejectReason: 'UNKNOWN_ACTION' };
  },

  onHostAction(ctx, _action: HostActionPayload) {
    return { runtime: ctx.runtime };
  },
};

export function demoMathEquationPayload(): MathEquationPayload {
  return validateMathEquationPayload({
    instruction: 'Encuentra el valor de la incógnita',
    equationLatex: '2x + 5 = \\square',
    resolutionSteps: ['Resta 5 en ambos miembros: 2x = 3', 'Divide por 2: x = \\frac{3}{2}'],
  });
}
